import { useEffect, useMemo } from 'react'
import type { JSX } from 'react'
import { startOfWeek } from 'date-fns'
import { Link } from 'react-router-dom'

import { clsx } from 'clsx'

import {
  Button,
  ChannelAvatar,
  CircleCheckBigIcon,
  ChevronRightIcon,
  MessageCircleIcon,
  EmptyState,
  Link as PopcornLink,
  Skeleton,
  Text,
  WarningIcon,
} from '@buffer-mono/popcorn'

import { useCommentsChannels } from '~publish/components/CommentsSidebar/useCommentsChannels'
import { useChannelsConnected } from '~publish/contexts/AccountContext'
import { useCommunityCountsContext } from '~publish/contexts/CommunityCountsContext'
import { readFragment } from '~publish/graphql'
import { useWeekStartsOn } from '~publish/hooks/useWeekStartsOn'
import { commentDetailsRoute } from '~publish/legacy/routes'
import { EngagementMedia } from '~publish/pages/Community/components/EngagementMedia'
import { useChannelCommentCounts } from '~publish/pages/Community/hooks/comments/useChannelCommentCounts'
import { CommentMediaAsset_Asset } from '~publish/pages/Community/Comments/fragments/commentLastReply'
import { useCommentsQuery } from '~publish/pages/Community/hooks/comments/useCommentsQuery'
import type { CommentsQuery } from '~publish/pages/Community/hooks/comments/useCommentsQuery'
import { useCommunityConfig } from '~publish/pages/Community/CommunityConfig'
import { PusherEvent } from '~publish/services/pusher'
import { canPerform } from '~publish/utils/permissions/canPerform'

import { SectionHeader } from '../SectionHeader'
import { useLastItemCount } from '../useLastItemCount'

import { useRealtimeRefetch } from '../useRealtimeRefetch'

import { getSlotIds } from '~publish/helpers/getSlotIds'

import styles from './CommentsSection.module.css'

export const RECENT_COMMENTS_LIMIT = 3

export const COMMENTS_LAST_COUNT_STORAGE_KEY = 'home-comments-last-count'

export const COMMENTS_UNSUPPORTED_CHANNELS_DESCRIPTION =
  "Connect a channel that's supported in Community and your comments will show up here."

/**
 * Sort must not lead with status when a specific status is filtered: the
 * backend's status-sorted bucket scan discards the status filter (it queries
 * every status bucket), so the default `unansweredFirst` sort would let
 * replied/dismissed comments leak back in. Mirrors the coercion in
 * `~publish/pages/Community/CommunityFilters/useFilters.ts`.
 */
const UNANSWERED_FILTER = {
  status: 'unreplied' as const,
  sortOption: 'newest' as const,
}

/**
 * The weekly count is an existence check ("did any comments arrive this
 * week?"), so the backend only needs to count up to 2 per channel. Using 2
 * instead of 1 keeps the hook's Pusher optimisation (skip refetch at
 * `limit - 1`) from suppressing the 0 → 1 update when the first comment of
 * the week arrives.
 */
const WEEKLY_COUNT_LIMIT = 2

const COMMENTS_REALTIME_EVENTS = [
  PusherEvent.COMMENT_INGESTED,
  PusherEvent.COMMENT_REPLIED,
  PusherEvent.COMMENT_DISMISSED,
  PusherEvent.COMMENT_UNDISMISSED,
  PusherEvent.DISMISS_ALL_COMMENTS_BY_POST,
  PusherEvent.DISMISS_ALL_COMMENTS_BY_CHANNEL,
  PusherEvent.DISMISS_ALL_COMMENTS_BY_ORGANIZATION,
  PusherEvent.DISMISS_ALL_COMMENTS_FOR_CHANNELS,
]

type CommentNode = CommentsQuery['comments']['edges'][number]['node']

export function CommentsSection(): JSX.Element {
  const channels = useChannelsConnected()
  const hasChannels = channels.length > 0

  // Uses `useCommentsChannels` rather than `useChannelsConnected` because the
  // support check reads `type` and the counts hook reads `allowedActions`,
  // neither of which the account query selects.
  const { channels: commentsChannels, loading: commentsChannelsLoading } =
    useCommentsChannels()
  const {
    config: { isSupportedInComments, channelsSettings },
    loading: configLoading,
    error: configError,
  } = useCommunityConfig()

  // `channelsSettings` is null until the configuration query resolves, and
  // `isSupportedInComments` reads every channel as unsupported until then. A
  // failed configuration query leaves it null for good, so that case must not
  // be read as "no supported channels".
  const supportKnown = channelsSettings !== null
  const configFailed = !configLoading && configError !== null

  const supportedChannels = useMemo(
    () => commentsChannels.filter(isSupportedInComments),
    [commentsChannels, isSupportedInComments],
  )
  const hasOnlyUnsupportedChannels =
    supportKnown &&
    commentsChannels.length > 0 &&
    supportedChannels.length === 0

  // The comments query rejects a batch where no channel grants ViewComments,
  // so unsupported and non-viewable channels must never reach it.
  const channelIds = useMemo(
    () =>
      supportedChannels
        .filter((channel) => canPerform(channel, 'viewComments'))
        .map((channel) => channel.id),
    [supportedChannels],
  )

  const { data, loading, error, fetchNewComments } = useCommentsQuery({
    channelIds,
    filters: UNANSWERED_FILTER,
    skip: channelIds.length === 0,
  })
  const { totalOrgCount: unansweredCount, error: countsError } =
    useCommunityCountsContext()

  // The counts context runs a query of its own and reads a failed fetch as 0.
  // A failure in that query, in the configuration query that scopes it, or in
  // the comments list below the header, makes the number untrustworthy, so the
  // header must not show one.
  const countUnavailable =
    Boolean(countsError) || Boolean(error) || configFailed

  // Whether any comments (regardless of status) arrived this calendar week —
  // decides between the "all caught up" and "no comments yet" empty states.
  const weekStartsOn = useWeekStartsOn() === 'monday' ? 1 : 0
  const weekStart = useMemo(
    () => startOfWeek(new Date(), { weekStartsOn }).toISOString(),
    [weekStartsOn],
  )
  const { counts: weeklyCounts, loading: weeklyCountsLoading } =
    useChannelCommentCounts({
      channels: supportedChannels,
      dateStart: weekStart,
      limit: WEEKLY_COUNT_LIMIT,
    })
  const hadCommentsThisWeek = (weeklyCounts ?? []).some(
    ({ count }) => count > 0,
  )

  useRealtimeRefetch({
    events: COMMENTS_REALTIME_EVENTS,
    refetch: fetchNewComments,
    allowedChannelIds: channelIds,
  })

  const comments = (data?.comments?.edges ?? [])
    .slice(0, RECENT_COMMENTS_LIMIT)
    .map((edge) => edge.node)

  const hasComments = comments.length > 0
  const [lastCommentsCount, setLastCommentsCount] = useLastItemCount(
    COMMENTS_LAST_COUNT_STORAGE_KEY,
  )

  const isLoading =
    loading ||
    commentsChannelsLoading ||
    weeklyCountsLoading ||
    (!supportKnown && !configFailed)

  // Remember the loaded row count so the next page load can size the
  // loading skeleton to this section's real geometry.
  useEffect(() => {
    if (isLoading || error || configFailed) return
    setLastCommentsCount(comments.length)
  }, [isLoading, error, configFailed, comments.length, setLastCommentsCount])

  return (
    <div className={styles.section}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <SectionHeader>Comments</SectionHeader>
          {hasChannels &&
            (countUnavailable ? (
              <Text
                as="span"
                size="sm"
                color="subtle"
                className={styles.countUnavailable}
              >
                <span className={styles.leadingSeparator}>·</span>
                <WarningIcon size="xsmall" />
                Count unavailable
              </Text>
            ) : (
              <Text as="span" size="sm" color="subtle">
                <span className={styles.leadingSeparator}>· </span>
                {unansweredCount} unanswered
              </Text>
            ))}
        </div>
        {hasChannels && (
          <Button
            as={Link}
            to="/community"
            size="small"
            variant="tertiary"
            className={styles.seeAll}
            aria-label="View all comments"
          >
            View All
            <ChevronRightIcon />
          </Button>
        )}
      </div>

      {isLoading && !hasComments ? (
        <CommentsSkeleton rows={lastCommentsCount} />
      ) : hasOnlyUnsupportedChannels ? (
        <CommentsUnsupportedChannelsState />
      ) : (error || configFailed) && !hasComments ? (
        <CommentsErrorState />
      ) : !hasComments && hadCommentsThisWeek ? (
        <CommentsCaughtUpState />
      ) : !hasComments ? (
        <CommentsEmptyState />
      ) : (
        <ul className={styles.list}>
          {comments.map((comment) => (
            <CommentRow comment={comment} key={comment.id} />
          ))}
        </ul>
      )}
    </div>
  )
}

function CommentsSkeleton({ rows }: { rows: number | null }): JSX.Element {
  // Zero or unknown counts reserve the empty state's geometry: the caught-up,
  // empty, and no-channel outcomes all render the same 200px empty state.
  const isEmptySized = rows === null || rows === 0
  const rowCount = isEmptySized ? 2 : Math.min(rows, RECENT_COMMENTS_LIMIT)

  return (
    <div
      className={clsx(styles.list, isEmptySized && styles.listEmptySized)}
      // Skeleton visibility is announced as live status, not form output.
      // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
      role="status"
      aria-label="Loading comments"
    >
      {getSlotIds('comment-row-skeleton', rowCount).map((skeletonId) => (
        <Skeleton
          key={skeletonId}
          height={72}
          className={styles.skeletonRow}
          data-testid="comments-skeleton"
        />
      ))}
    </div>
  )
}

function CommentsCaughtUpState(): JSX.Element {
  return (
    <EmptyState
      size="small"
      variant="success"
      // Completion feedback is announced as live status, not form output.
      // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
      role="status"
      className={styles.emptyState}
    >
      <EmptyState.Icon>
        <CircleCheckBigIcon />
      </EmptyState.Icon>
      <EmptyState.Heading>You&apos;re all caught up! 🎉</EmptyState.Heading>
      <EmptyState.Description>
        We&apos;ll let you know when new comments appear.
      </EmptyState.Description>
    </EmptyState>
  )
}

function CommentsEmptyState(): JSX.Element {
  return (
    <EmptyState size="small" className={styles.emptyState}>
      <EmptyState.Icon>
        <MessageCircleIcon />
      </EmptyState.Icon>
      <EmptyState.Description>
        No comments yet.
        <br />
        You&apos;ll see the latest comments here.
      </EmptyState.Description>
    </EmptyState>
  )
}

function CommentsUnsupportedChannelsState(): JSX.Element {
  return (
    <EmptyState size="small" className={styles.emptyState}>
      <EmptyState.Icon>
        <MessageCircleIcon />
      </EmptyState.Icon>
      <EmptyState.Description className={styles.emptyStateDescription}>
        {COMMENTS_UNSUPPORTED_CHANNELS_DESCRIPTION}
      </EmptyState.Description>
    </EmptyState>
  )
}

function CommentsErrorState(): JSX.Element {
  return (
    <EmptyState
      size="small"
      variant="warning"
      role="alert"
      className={styles.emptyState}
    >
      <EmptyState.Icon>
        <WarningIcon />
      </EmptyState.Icon>
      <EmptyState.Heading>
        Couldn&apos;t load your recent comments
      </EmptyState.Heading>
      <EmptyState.Description>
        Please{' '}
        <PopcornLink as="button" onClick={() => window.location.reload()}>
          refresh this page
        </PopcornLink>
        .
      </EmptyState.Description>
    </EmptyState>
  )
}

type CommentRowProps = {
  comment: CommentNode
}

function CommentRow({ comment }: CommentRowProps): JSX.Element {
  const handle = comment.author.username || comment.author.name || ''
  const commentUrl = commentDetailsRoute.getRoute({ commentId: comment.id })

  return (
    <li>
      <Link
        to={commentUrl}
        className={styles.row}
        aria-label={
          handle
            ? `Open comment from @${handle} to reply`
            : 'Open comment to reply'
        }
      >
        <div className={styles.rowHeader}>
          <ChannelAvatar
            size="small"
            alt=""
            src={comment.author.avatar ?? undefined}
            channel={comment.serviceType}
            className={styles.avatar}
          />
          {handle && (
            <Text
              size="sm"
              color="subtle"
              className={styles.handle}
              translate="no"
            >
              @{handle}
            </Text>
          )}
        </div>
        {comment.text ? (
          <Text className={styles.commentText} translate="no">
            {comment.text}
          </Text>
        ) : null}
        <EngagementMedia
          assets={[...readFragment(CommentMediaAsset_Asset, comment.assets)]}
        />
      </Link>
    </li>
  )
}
